import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArchiveIcon, StoreIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useSalesChannels } from '@/api/sales-channels'
import type { SalesChannel } from '@/api/sales-channels'
import { useWorkspace } from '@/api/workspace'
import { FilterBar } from '@/components/filter-bar'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

// Sales Channels list (UI-SCREENS S-01, 05a #169): name, kind, units at channel and the archived
// badge behind the Archived toggle. New Sales Channel is Owner only. Last report period and
// outstanding come with 06.
//
// The Archived filter sits in the shared filter bar (as on I-03) and lives in the URL.
const searchSchema = z.object({
  archived: z.boolean().optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/channels/')({
  component: SalesChannelsList,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'salesChannels.title' },
})

function SalesChannelsList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const workspace = useWorkspace(slug)
  const { archived = false } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const channels = useSalesChannels(slug, { archived })

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('salesChannels.title')}</h1>
        {workspace.data?.role === 'owner' ? (
          <Button asChild className="h-11">
            <Link to="/w/$slug/channels/new" params={{ slug }}>
              {t('salesChannels.newChannel')}
            </Link>
          </Button>
        ) : null}
      </div>

      <FilterBar
        fields={[
          {
            key: 'archived',
            label: t('filterBar.archived'),
            icon: ArchiveIcon,
            value: archived ? 'true' : undefined,
            options: [{ value: 'true', label: t('filterBar.shown') }],
            onChange: (value) => navigate({ search: value === 'true' ? { archived: true } : {}, replace: true }),
          },
        ]}
        onClearAll={() => navigate({ search: {}, replace: true })}
      />

      {channels.isPending ? (
        <LoadingSkeleton />
      ) : channels.isError || !channels.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : channels.data.length === 0 ? (
        <RecordListEmpty>{t('salesChannels.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {channels.data.map((channel) => (
            <SalesChannelRow key={channel.id} slug={slug} channel={channel} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

function SalesChannelRow({ slug, channel }: { slug: string; channel: SalesChannel }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      title={
        <RecordLink to="/w/$slug/channels/$channelId" params={{ slug, channelId: String(channel.id) }}>
          {channel.name}
        </RecordLink>
      }
      badges={channel.archived_at ? <Badge variant="outline">{t('salesChannels.archived')}</Badge> : null}
      meta={[
        {
          icon: StoreIcon,
          label: t('salesChannels.detail.kind'),
          value: t(channel.kind === 'direct' ? 'salesChannels.kinds.direct' : 'salesChannels.kinds.consignment'),
        },
      ]}
      figure={t('salesChannels.list.units', { count: channel.units_at_channel })}
      caption={t('salesChannels.detail.stock')}
    />
  )
}
